"use client"

import { useEffect } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { m, AnimatePresence } from "framer-motion"
import { ProFormaForm } from "./pro-forma-form"
import type { Answers, Recommendation } from "@/lib/quiz/types"
import type { PriceMap } from "@/lib/pricing"

interface Props {
  open: boolean
  onClose: () => void
  answers: Answers
  recommendation: Recommendation
  totalPriceChf: number
  prices: PriceMap
}

export function ProFormaModal({
  open,
  onClose,
  answers,
  recommendation,
  totalPriceChf,
  prices,
}: Props) {
  const t = useTranslations("quiz")
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handler)
    }
  }, [open, onClose])

  return (
    // safe-animate-presence: conditional-mount — children only render when `open=true`
    <AnimatePresence>
      {open && (
        <>
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <m.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            className="fixed inset-3 z-[101] flex flex-col overflow-auto bg-white md:inset-auto md:top-1/2 md:left-1/2 md:max-h-[92vh] md:-translate-x-1/2 md:-translate-y-1/2"
            style={{
              borderRadius: 24,
              maxWidth: 560,
              width: "calc(100% - 24px)",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-neutral-100 transition-colors hover:bg-neutral-200"
              aria-label={t("closeLabel")}
              autoFocus
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="p-6 pt-12 md:p-8 md:pt-12">
              <ProFormaForm
                answers={answers}
                recommendation={recommendation}
                totalPriceChf={totalPriceChf}
                prices={prices}
              />
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  )
}
